import { useEffect, useMemo, useRef, useState } from 'react';
import { useAccessToken } from '../../../hooks/useAccessToken';
import { useLogger } from '../../../context/useLogger';
import { EdiDsnBilansFileService } from '../../../services/EdiDsnBilansFileService';
import type { DsnBilan } from '../../../types/bilan.types';

export type BilanContentState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'empty' }
  | { status: 'zip-downloaded' }
  | { status: 'ready'; blobUrl: string; kind: 'pdf' | 'html' };

type BilanData = DsnBilan | Record<string, never>;

export function useBilanContent(bilan: BilanData | undefined, enabled: boolean): BilanContentState {
  const getAccessToken = useAccessToken();
  const logger = useLogger();

  const ediBilanFileService = useMemo(
    () => new EdiDsnBilansFileService(getAccessToken, logger),
    [getAccessToken, logger],
  );

  const [contentState, setContentState] = useState<BilanContentState>({ status: 'loading' });
  const zipDownloadStarted = useRef(false);

  useEffect(() => {
    if (!enabled || !bilan || Object.keys(bilan).length === 0) {
      return;
    }
    const currentBilan = bilan;
    const currentBilanInstanceId = currentBilan.InstanceId;

    let cancelled = false;
    let createdBlobUrl: string | null = null;

    async function loadContent() {
      if (currentBilan.IsZipFile) {
        if (zipDownloadStarted.current) {
          return;
        }
        zipDownloadStarted.current = true;

        const result =
          await ediBilanFileService.tryFetchBilanFileRawContent(currentBilanInstanceId);
        if (cancelled) return;

        if (!result.success) {
          setContentState({
            status: 'error',
            message: 'Erreur lors du t\u00e9l\u00e9chargement du bilan',
          });
          return;
        }

        const blob = new Blob([result.data], { type: 'application/zip' });
        if (blob.size === 0) {
          setContentState({ status: 'empty' });
          return;
        }

        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.style.display = 'none';
        anchor.href = url;
        anchor.download = `bilan-${currentBilanInstanceId}.zip`;
        document.body.appendChild(anchor);
        anchor.click();
        document.body.removeChild(anchor);
        URL.revokeObjectURL(url);

        setContentState({ status: 'zip-downloaded' });
        return;
      }

      if (currentBilan.IsPdfFile) {
        const result =
          await ediBilanFileService.tryFetchBilanFileRawContent(currentBilanInstanceId);
        if (cancelled) return;

        if (!result.success) {
          setContentState({
            status: 'error',
            message: 'Erreur lors de la r\u00e9cup\u00e9ration du PDF',
          });
          return;
        }

        const blob = new Blob([result.data], { type: 'application/pdf' });
        if (blob.size === 0) {
          setContentState({ status: 'empty' });
          return;
        }

        createdBlobUrl = URL.createObjectURL(blob);
        setContentState({ status: 'ready', blobUrl: createdBlobUrl, kind: 'pdf' });
        return;
      }

      const result = await ediBilanFileService.tryFetchBilanFileHtmlContent(currentBilanInstanceId);
      if (cancelled) return;

      if (!result.success || !result.data) {
        setContentState({
          status: 'error',
          message: 'Impossible de r\u00e9cup\u00e9rer le document',
        });
        return;
      }

      if (!result.data.content || result.data.content.trim() === '') {
        setContentState({ status: 'empty' });
        return;
      }

      const blob = new Blob([result.data.content], { type: 'text/html;charset=utf-8' });
      createdBlobUrl = URL.createObjectURL(blob);
      setContentState({ status: 'ready', blobUrl: createdBlobUrl, kind: 'html' });
    }

    loadContent().catch(error => {
      logger.error('Error loading bilan content:', error);
      if (!cancelled) {
        setContentState({
          status: 'error',
          message: 'Une erreur est survenue lors de la r\u00e9cup\u00e9ration du bilan',
        });
      }
    });

    return () => {
      cancelled = true;
      if (createdBlobUrl) {
        URL.revokeObjectURL(createdBlobUrl);
      }
    };
  }, [bilan, ediBilanFileService, enabled, logger]);

  return contentState;
}
